import { DeviceIdentity } from '../devices/DeviceIdentity';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { todayCategories, todayStates, type TodayCategory, type TodayState, type TodayView } from '@api/today/contracts';
import { getToday } from '../lib/api';
import { absolute } from '../lib/time';
import { FilterBar } from '../filters/FilterBar';
import { useCatalog } from '../search/hooks';
import { useScope } from '../scope/scope';
import { Card, Skeleton } from '../ui/misc';
import { ErrorPanel } from '../ui/errors';
import { Button } from '../ui/Button';
import { useTodayScope } from './today-scope';

const states: Record<TodayState, string> = { not_assessed: 'Not assessed / stale', available: 'Available', awaiting_decision: 'Awaiting decision',
  scheduled: 'Approved / scheduled', running: 'Running', verifying: 'Verifying / outcome unknown', reboot_required: 'Reboot / relaunch required',
  failed: 'Failed / missed window', verified_complete: 'Verified complete', reported_success: 'Reported success' };
const categories: Record<TodayCategory, string> = { assessment: 'Evidence coverage', availability: 'Offline / unreported', protection: 'Protection gaps', repair: 'Repairs and diagnostics',
  os_quality: 'OS patch work', os_feature: 'OS feature upgrades', cached_software: 'Other cached software offers', installed_app: 'Installed-app updates', optional_app: 'Optional app deployments',
  driver: 'Drivers', protection_agent: 'Protection-agent updates', agent: 'WITTCO Agent updates', ticket: 'Tickets' };

export function DailyWork() {
  const { mspId } = useScope();
  const scope = useTodayScope();
  const catalog = useCatalog();
  const input = { scope: scope.query, state: scope.workState, category: scope.category, limit: 25, offset: (scope.page - 1) * 25 };
  const daily = useQuery({ queryKey: ['scope', mspId, 'today', input], enabled: !scope.invalid.length,
    queryFn: ({ signal }) => getToday(mspId, input, signal), refetchInterval: 30_000 });
  const pages = Math.max(1, Math.ceil((daily.data?.total ?? 0) / 25));
  useEffect(() => { if (daily.data && scope.page > pages) scope.setWork('workPage', String(pages)); }, [daily.data, scope.page, pages]);
  return <section aria-labelledby="daily-work" className="min-w-0 space-y-3">
    <h2 id="daily-work" className="text-lg font-semibold">Work and decisions</h2>
    <FilterBar state={scope.state} onChange={scope.update} catalog={catalog.catalog} fields={['org', 'site', 'os', 'status', 'seen', 'signal', 'software', 'duplicateName']} />
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="min-w-0 text-[13px]">Work state
        <select aria-label="Work state" className="mt-1 block h-11 w-full rounded-lg border border-line bg-panel px-2" value={scope.workState ?? ''} onChange={e => scope.setWork('workState', e.target.value)}>
          <option value="">Actionable work</option>{todayStates.map(s => <option key={s} value={s}>{states[s]}</option>)}
        </select>
      </label>
      <label className="min-w-0 text-[13px]">Work type
        <select aria-label="Work type" className="mt-1 block h-11 w-full rounded-lg border border-line bg-panel px-2" value={scope.category ?? ''} onChange={e => scope.setWork('category', e.target.value)}>
          <option value="">All types</option>{todayCategories.map(c => <option key={c} value={c}>{categories[c]}</option>)}
        </select>
      </label>
    </div>
    {scope.invalid.length ? <Card className="p-4">Invalid scope filter. Clear the invalid link filters before viewing work. <Link to={`/w/${mspId}/today`} className="underline">Clear filters</Link></Card>
      : daily.isError ? <Card><ErrorPanel error={daily.error} onRetry={() => void daily.refetch()} /></Card>
        : daily.isPending ? <Skeleton className="h-24 w-full" /> : <>
          <DailyCards data={daily.data} mspId={mspId} />
          <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
            <span>{daily.data.total} matching items · page {scope.page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={scope.page <= 1} onClick={() => scope.setWork('workPage', String(scope.page - 1))}>Previous</Button>
              <Button variant="secondary" disabled={scope.page >= pages || scope.page >= 401} onClick={() => scope.setWork('workPage', String(scope.page + 1))}>Next</Button>
            </div>
          </div>
        </>}
  </section>;
}

export function DailyCards({ data, mspId }: { data: TodayView; mspId: string }) {
  const unresolved = todayStates.filter(s => s !== 'verified_complete' && s !== 'reported_success').reduce((n, s) => n + (data.counts[s] ?? 0), 0);
  return <>
    <Card className="space-y-2 p-4 text-[13px]">
      <p className="font-semibold">Last 24 hours: {data.digest.verifiedComplete} verified complete · {data.digest.reportedSuccess} reported success{data.digest.reportedSuccess === 1 ? '' : 'es'}</p>
      <p>Remaining in this device scope: {unresolved} unresolved items. These counts include every work state and type.</p>
      <p>{data.coverage.freshDevices} of {data.coverage.totalDevices} devices have a recent check-in and inventory.</p>
      {data.partial && <p role="status" className="text-attention">Counts are lower bounds. Source limits reached; narrow customer/site filters to see remaining work.</p>}
      <p className="text-ink-3">As of {absolute(data.computedAt)}. Refreshes every 30 seconds; recorded evidence is not a live scan.</p>
    </Card>
    <div className="grid min-w-0 gap-3 lg:grid-cols-2">
      {data.items.map(item => {
        const target = item.target;
        const href = target.kind === 'job' ? `/w/${mspId}/work?job=${encodeURIComponent(target.id)}`
          : target.kind === 'ticket' ? `/w/${mspId}/tickets/${target.id}` : `/w/${mspId}/devices/${target.id}?tab=${target.tab ?? 'overview'}`;
        return <article key={item.id} className="min-w-0 space-y-2 rounded-xl border border-line bg-panel p-4 text-[13px] [overflow-wrap:anywhere]">
          <p className="text-[12px] text-ink-3">{categories[item.category]} · <span className={item.state === 'failed' ? 'text-offline' : 'text-ink-2'}>{states[item.state]}</span></p>
          <h3 className="font-semibold text-ink">{item.title}</h3>
          <p className="text-ink-2">{[item.customer ?? 'Unmatched customer', item.site, item.device ?? (item.deviceId ? 'Device' : 'Customer scope')].filter(Boolean).join(' · ')}</p>
          {item.deviceId && <DeviceIdentity device={{ id: item.deviceId, title: item.device ?? 'Device' }} />}
          <p>{item.reason}</p>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-ink-3">
            <dt>Owner</dt><dd>{item.owner ? `Recorded person ${item.owner}` : 'Unassigned'}</dd>
            <dt>Due / next run</dt><dd>{item.dueAt ? absolute(item.dueAt) : 'Not scheduled'}</dd>
            <dt>Evidence</dt><dd>{item.observedAt ? absolute(item.observedAt) : 'Not reported'}</dd>
          </dl>
          <Link to={href} className="inline-flex min-h-11 items-center font-medium text-brand hover:underline">{item.nextAction}</Link>
        </article>;
      })}
    </div>
    {!data.items.length && <Card className="p-4 text-[13px]">No matching work. Review evidence coverage; an empty queue does not establish compliance.</Card>}
    <details className="rounded-xl border border-line bg-panel p-4 text-[13px]">
      <summary className="cursor-pointer font-medium">Sources and limits</summary>
      <ul className="mt-3 space-y-2">{data.sources.map(s => <li key={s.category}><strong>{categories[s.category]}: {s.state === 'available' ? 'Existing source' : 'Not assessed'}</strong> · {s.detail}</li>)}</ul>
      {data.limitations.map(text => <p key={text} className="mt-2 text-ink-3">{text}</p>)}
    </details>
  </>;
}
